import { useCallback, useMemo, useRef, useState } from "react";

export interface VideoController {
  /** Attach to the <video> element. */
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isReady: boolean;
  seekTo: (t: number) => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  /** Event handlers the player component spreads onto the <video>. */
  bind: {
    onTimeUpdate: React.ReactEventHandler<HTMLVideoElement>;
    onLoadedMetadata: React.ReactEventHandler<HTMLVideoElement>;
    onCanPlay: React.ReactEventHandler<HTMLVideoElement>;
    onDurationChange: React.ReactEventHandler<HTMLVideoElement>;
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
  };
}

// onTimeUpdate fires several times/sec; only publish when it moves enough to
// matter for highlighting/readouts, to avoid thrashing every consumer.
const MIN_TIME_DELTA = 0.05;

/**
 * Single owner of the editor's <video> element. Holds playback state and
 * exposes an imperative `seekTo` so the transcript list, range selector, and
 * controls all share one source of truth (mirrors the currentTimeStamp +
 * seek pattern from the read-only TranscriptViewer).
 */
export const useVideoController = (): VideoController => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const lastPublished = useRef(0);

  const seekTo = useCallback((t: number) => {
    const video = videoRef.current;
    const max = video?.duration;
    const clamped = Math.max(
      0,
      typeof max === "number" && Number.isFinite(max) ? Math.min(t, max) : t,
    );
    if (video) video.currentTime = clamped;
    lastPublished.current = clamped;
    setCurrentTime(clamped);
  }, []);

  const play = useCallback(() => {
    videoRef.current?.play().catch(() => {});
  }, []);
  const pause = useCallback(() => {
    videoRef.current?.pause();
  }, []);
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }, []);

  const bind = useMemo(
    () => ({
      onTimeUpdate: (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const t = e.currentTarget.currentTime;
        if (Math.abs(t - lastPublished.current) >= MIN_TIME_DELTA) {
          lastPublished.current = t;
          setCurrentTime(t);
        }
      },
      onLoadedMetadata: (e: React.SyntheticEvent<HTMLVideoElement>) =>
        setDuration(e.currentTarget.duration || 0),
      onDurationChange: (e: React.SyntheticEvent<HTMLVideoElement>) =>
        setDuration(e.currentTarget.duration || 0),
      onCanPlay: () => setIsReady(true),
      onPlay: () => setIsPlaying(true),
      onPause: () => setIsPlaying(false),
      onEnded: () => setIsPlaying(false),
    }),
    [],
  );

  return {
    videoRef,
    currentTime,
    duration,
    isPlaying,
    isReady,
    seekTo,
    play,
    pause,
    togglePlay,
    bind,
  };
};
